import * as Sentry from "@sentry/react";
import { useCallback } from "react";
import NoSleep from "nosleep.js";
import { getValue, setValue } from "@/utils/storage";
import { captureError } from "@/utils/logger";
import { getOrCreateUserInfo } from "./get-or-create-user-info";
import {
  ConnectionDetails,
  useGlobalState as useGlobalStateContext,
} from "../providers/GlobalStateProvider";

type ConnectOptions = {
  newRoom?: boolean;
};

export function useGlobalState() {
  const { state, dispatch } = useGlobalStateContext();

  const setChatMode = useCallback(
    (mode: "voice" | "text" | null) => {
      if (state.chatMode === mode) {
        return;
      }

      dispatch({ type: "SET_CHAT_MODE", payload: mode });
    },
    [state.chatMode, dispatch],
  );
  const setIsMicrophoneEnabled = useCallback(
    (value: boolean) => {
      if (state.isMicrophoneEnabled === value) {
        return;
      }

      dispatch({ type: "SET_MICROPHONE_ENABLED", payload: value });
    },
    [state.isMicrophoneEnabled, dispatch],
  );
  const setShowReconnectPrompt = useCallback(
    (value: boolean) => {
      if (state.showReconnectPrompt === value) {
        return;
      }

      dispatch({ type: "SET_SHOW_RECONNECT_PROMPT", payload: value });
    },
    [state.showReconnectPrompt, dispatch],
  );
  const setConnectionDetails = useCallback(
    (value: ConnectionDetails | undefined) => {
      if (
        state.connectionDetails?.participantToken === value?.participantToken
      ) {
        return;
      }

      dispatch({ type: "SET_CONNECTION_DETAILS", payload: value });
    },
    [state.connectionDetails, dispatch],
  );
  const setStoreUrl = useCallback(
    (value: string) => {
      if (state.storeUrl === value) {
        return;
      }

      dispatch({ type: "SET_STORE_URL", payload: value });
    },
    [state.storeUrl, dispatch],
  );
  const setCustomerId = useCallback(
    (value: string) => {
      if (state.customerId === value) {
        return;
      }

      dispatch({ type: "SET_CUSTOMER_ID", payload: value });
    },
    [state.customerId, dispatch],
  );

  const connect = useCallback(
    async (opts?: ConnectOptions) => {
      const noSleep = new NoSleep();
      noSleep.enable();
      Sentry.captureMessage("request room");
      const url = new URL(
        `${import.meta.env.VITE_BACKEND_API}/api/connection-details`,
        window.location.origin,
      );
      const { identity } = getOrCreateUserInfo();

      url.searchParams.append("customer_id", state.customerId ?? "");
      url.searchParams.append("store_url", state.storeUrl ?? "");
      url.searchParams.append("identity", identity);

      const lastInteraction = getValue("last_interaction");
      const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes

      // Only set room name if the last interaction was less than 5 minutes ago
      if (
        lastInteraction &&
        Date.now() - parseInt(lastInteraction) < SESSION_TIMEOUT &&
        !opts?.newRoom
      ) {
        setValue("last_interaction", Date.now().toString());
        url.searchParams.append("room_name", getValue("room_name") ?? "");
      }

      try {
        const response = await fetch(url.toString());
        const data = await response.json();

        setValue("hasRequestedMic", "true");
        setValue("room_name", data.roomName);

        setConnectionDetails(data);
      } catch (error) {
        captureError("Failed to fetch connection details:", error);
      }
    },
    [state.customerId, state.storeUrl, setConnectionDetails],
  );

  const reconnect = useCallback(
    (opts?: ConnectOptions) => {
      setConnectionDetails(undefined);
      connect(opts);
    },
    [connect, setConnectionDetails],
  );

  const setMicPermission = useCallback(
    (status: "granted" | "prompt" | "denied") => {
      if (state.micPermission === status) {
        return;
      }

      dispatch({ type: "SET_MIC_PERMISSION", payload: status });
    },
    [state.micPermission, dispatch],
  );

  return {
    ...state,
    setChatMode,
    setIsMicrophoneEnabled,
    setMicPermission,
    setShowReconnectPrompt,
    setConnectionDetails,
    setStoreUrl,
    setCustomerId,
    connect,
    reconnect,
  };
}
